import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { DashboardJob } from '../api/client';
import { updateApplicationResume, uploadApplicationResume } from '../api/resumeApi';
import type {
  ApplicationStatus,
  ConfigData,
  DashboardMetricsResponse,
  DeadLetterListParams,
  DeadLetterStats,
  SetupVerifyResponse,
  AiCostMetrics,
  AICostDashboardData,
  SyncStatusResponse,
  FeedbackType,
  ReviewVersionsResponse,
  NoteType,
  InterviewNotesResponse,
  ReviewFeedbackData,
  ReviewFeedbackEntry,
  ScreeningConflictsResponse,
  QuickRating,
} from '../types';

import type { RejectionReason, CandidateMergeAudit, PaginatedResponse, ReportType, ReportSnapshotSummary, ReportSnapshotFull, DashboardAnalytics, AiAccuracyReportResponse } from '../types';
import type { ModelCatalogItem, ModelCatalogResponse } from '../types';
import type { UserRole } from '../types/auth';

// Re-export for consumers
export type { ModelCatalogItem, ModelCatalogResponse };
export type { DashboardJob };

export interface OrgUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AdminOrg {
  id: string;
  slug: string;
  name: string;
  status: 'pending' | 'active' | 'suspended';
  userCount: number;
  createdAt: string;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  organizationId: string | null;
  organizationName: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

// Query Keys
export const queryKeys = {
  jobs: ['jobs'] as const,
  jobsDashboard: () => ['jobs', 'dashboard'] as const,
  unreadSignals: ['signals', 'unread'] as const,
  job: (id: number) => ['jobs', id] as const,
  candidates: ['candidates'] as const,
  candidate: (id: number) => ['candidates', id] as const,
  applications: ['applications'] as const,
  application: (id: number) => ['applications', id] as const,
  reviews: ['reviews'] as const,
  review: (id: number) => ['reviews', id] as const,
  quickRate: (applicationId: number) => ['quick-rate', applicationId] as const,
  syncStatus: (reviewId: number) => ['syncStatus', reviewId] as const,
  health: ['health'] as const,
  systemHealth: ['systemHealth'] as const,
  config: ['config'] as const,
  dashboard: ['dashboard'] as const,
  deadLetter: ['deadLetter'] as const,
  deadLetterEntry: (id: number) => ['deadLetter', id] as const,
  deadLetterStats: ['deadLetter', 'stats'] as const,
  setupVerify: ['setup', 'verify'] as const,
  aiCostMetrics: ['ai-costs', 'metrics'] as const,
  aiCostDashboard: ['ai-costs', 'dashboard'] as const,
  people: ['people'] as const,
  person: (id: number) => ['people', id] as const,
  personNotes: (personId: number) => ['people', personId, 'notes'] as const,
  feedback: (applicationId: number) => ['feedback', applicationId] as const,
  reviewVersions: (applicationId: number) => ['reviewVersions', applicationId] as const,
  jobCriteria: (jobId: number) => ['jobCriteria', jobId] as const,
  jobCriteriaChangelog: (jobId: number) => ['jobCriteria', jobId, 'changelog'] as const,
  screeningConflicts: ['dashboard', 'screening-conflicts'] as const,
  reviewFeedback: (reviewId: number) => ['reviewFeedback', reviewId] as const,
  rejectionReasons: ['rejectionReasons'] as const,
  mergeAudit: ['mergeAudit'] as const,
  talentPool: ['talentPool'] as const,
  talentPoolStats: ['talentPool', 'stats'] as const,
  reportSnapshots: ['reportSnapshots'] as const,
  reportSnapshot: (id: number) => ['reportSnapshots', id] as const,
  reportsDashboard: ['reportsDashboard'] as const,
  aiAccuracyReport: ['aiAccuracyReport'] as const,
  lookupBySrId: (srApplicationId: string) => ['lookupBySrId', srApplicationId] as const,
  funnelInsights: (jobId: number) => ['funnelInsights', jobId] as const,
  modelCatalog: ['modelCatalog'] as const,
  orgUsers: ['org-users'] as const,
  adminOrganizations: ['admin-organizations'] as const,
  adminUsers: ['admin-users'] as const,
};

// People Hooks

export interface UsePeopleParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  jobId?: number;
}

export function usePeople(params: UsePeopleParams = {}) {
  const { page = 1, limit = 20, search, status, jobId } = params;
  return useQuery({
    queryKey: [...queryKeys.people, { page, limit, search, status, jobId }],
    queryFn: () => api.getPeople(page, limit, search, status, jobId),
  });
}

export function usePerson(id: number) {
  return useQuery({
    queryKey: queryKeys.person(id),
    queryFn: () => api.getPerson(id),
    enabled: id > 0,
  });
}

// Talent Pool Hooks (US-088)

export interface UseTalentPoolParams {
  page?: number;
  limit?: number;
  search?: string;
}

export function useTalentPool(params: UseTalentPoolParams = {}) {
  const { page = 1, limit = 20, search } = params;
  return useQuery({
    queryKey: [...queryKeys.talentPool, { page, limit, search }],
    queryFn: () => api.getTalentPool(page, limit, search),
  });
}

export function useTalentPoolStats() {
  return useQuery({
    queryKey: queryKeys.talentPoolStats,
    queryFn: () => api.getTalentPoolStats(),
  });
}

export function useCreateTalentPoolCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      location?: string;
      notes?: string;
    }) => api.createTalentPoolCandidate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.talentPool });
    },
  });
}

export function useConvertToApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, jobId }: { candidateId: number; jobId: number }) =>
      api.convertToApplication(candidateId, jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.talentPool });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    },
  });
}

// Interview Notes Hooks

export function usePersonNotes(personId: number) {
  return useQuery<InterviewNotesResponse>({
    queryKey: queryKeys.personNotes(personId),
    queryFn: () => api.getPersonNotes(personId),
    enabled: personId > 0,
  });
}

export function useCreatePersonNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personId,
      content,
      noteType,
      applicationId,
    }: {
      personId: number;
      content: string;
      noteType: NoteType;
      applicationId?: number;
    }) => api.createPersonNote(personId, content, noteType, applicationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.personNotes(variables.personId) });
    },
  });
}

export function useDeletePersonNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personId,
      noteId,
    }: {
      personId: number;
      noteId: number;
    }) => api.deletePersonNote(personId, noteId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.personNotes(variables.personId) });
    },
  });
}

// People Management Hooks

export function useArchivePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.archivePerson(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

export function useReprocessPerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.reprocessPerson(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
      queryClient.invalidateQueries({ queryKey: queryKeys.person(id) });
    },
  });
}

export function useBatchArchivePeople() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (candidateIds: number[]) => api.batchArchivePeople(candidateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

export function useBatchReprocessPeople() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (candidateIds: number[]) => api.batchReprocessPeople(candidateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

// Application batch hooks

export function useBatchArchiveApplications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationIds: number[]) => api.batchArchiveApplications(applicationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    },
  });
}

export function useBatchReprocessApplications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationIds: number[]) => api.batchReprocessApplications(applicationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    },
  });
}

// US-555: no cache invalidation — the download doesn't change server-side state.
export function useDownloadResumes() {
  return useMutation({
    mutationFn: (applicationIds: number[]) => api.downloadResumes(applicationIds),
  });
}

// Jobs Hooks

export interface UseJobsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export function useJobs(params: UseJobsParams = {}) {
  const { page = 1, limit = 20, search, status } = params;
  return useQuery({
    queryKey: [...queryKeys.jobs, { page, limit, search, status }],
    queryFn: () => api.getJobs(page, limit, search, status),
  });
}

export function useJob(id: number) {
  return useQuery({
    queryKey: queryKeys.job(id),
    queryFn: () => api.getJob(id),
    enabled: id > 0,
  });
}

export function useTriggerJobSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.triggerJobSync(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

export function useBackfillPreview(jobId: number) {
  return useQuery({
    queryKey: [...queryKeys.jobs, jobId, 'backfill-preview'],
    queryFn: () => api.getBackfillPreview(jobId),
    enabled: jobId > 0,
  });
}

export function useStartBackfill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.startBackfill(id),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.jobs, jobId, 'backfill-preview'] });
    },
  });
}

export function useCancelBackfill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.cancelBackfill(id),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.jobs, jobId, 'backfill-preview'] });
    },
  });
}

export function useUpdateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { status?: string; allowedStages?: string[]; skipHumanReviewed?: boolean } }) =>
      api.updateJob(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
      queryClient.invalidateQueries({ queryKey: queryKeys.job(data.id) });
    },
  });
}

export function useUpdateSourcingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, data }: { jobId: number; data: { passiveSourcingEnabled?: boolean; sourcingMinStars?: number } }) =>
      api.updateSourcingSettings(jobId, data),
    onSuccess: (_data, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.job(jobId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.jobs, jobId, 'sourcing-matches'] });
    },
  });
}

/**
 * US-403 (C2 fix): `specVersion` is part of the pagination contract and MUST be
 * threadable from here. The route's 409 branch only fires when the client sends
 * the param, and this hook — the only path any page uses to reach
 * `getSourcingMatches` — previously had no field for it, so the entire stale-
 * spec guard was unreachable in the running app.
 */
export function useSourcingMatches(
  jobId: number,
  params: { limit?: number; offset?: number; minStars?: number; specVersion?: number } = {},
) {
  return useQuery({
    queryKey: [...queryKeys.jobs, jobId, 'sourcing-matches', params],
    queryFn: () => api.getSourcingMatches(jobId, params),
    enabled: jobId > 0,
  });
}

export function useSubmitSourcingFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, candidateId, feedback }: { jobId: number; candidateId: number; feedback: 'approved' | 'rejected' }) =>
      api.submitSourcingFeedback(jobId, candidateId, feedback),
    onSuccess: (_data, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.jobs, jobId, 'sourcing-matches'] });
    },
  });
}

// Blind-judgment calibration UI (US-377 frontend, Epic 49 Phase 1). The
// candidate rows returned here carry NOTHING AI-derived or similarity-derived:
// no score, no band, no rank, no similarity value, no match count. US-500
// deleted `cosineScore`/`scoreBand` from the route's SELECT and from the
// contract, so the leak is unrepresentable rather than merely un-rendered —
// the comment that used to sit here excused both fields as existing "for later
// round-end summary use", which was false (the summary reads /reveal) while
// the card was in fact rendering the band above the verdict buttons.
/**
 * The response shape is no longer re-declared here. It is the mirrored copy of
 * the server's own `CalibrationCandidatesResponse`
 * (`packages/web/src/types/calibration-api.ts` ↔
 * `packages/shared/src/types/calibration-api.ts`, guarded by a byte-for-byte
 * drift test), which the route annotates its reply with.
 *
 * That indirection is the fix for the failure this page already shipped once: a
 * locally-invented interface plus `data as unknown as ...` in the page let a
 * frontend be written against a contract the API never implemented, while both
 * test suites stayed green.
 *
 * `aiReviewScore`/`aiReviewId` are absent by design — the route never selects
 * them, so there is no field for the blind-judgment UI to leak — and the resume
 * field is `redactedResumeText`, never the raw document.
 */
import type {
  CalibrationCandidatesResponse,
  CalibrationJobSpecResponse,
  RefinementProposalResponse,
} from '../types/calibration-api';
import { isFailedCalibrationRound } from '../types/calibration-api';
export type {
  CalibrationCandidateRow,
  CalibrationCandidatesResponse,
  CalibrationJobContext,
  CalibrationRoundInfo,
  CalibrationIdentityResponse,
  CalibrationJobSpecResponse,
} from '../types/calibration-api';

/** Poll cadence while a calibration round is still being populated. */
export const CALIBRATION_CANDIDATES_POLL_MS = 10000;

/**
 * M11. Calibration rows land one at a time as each AI review finishes, so the
 * page's "Still screening the rest of this batch — N of M ready" line is stale
 * the moment it renders unless something refetches. Poll until the round is
 * full, then stop: a full round never gains rows, and this page can sit open
 * for hours.
 *
 * React Query v5 function form: `(query) => number | false`.
 */
export function calibrationCandidatesRefetchInterval(query: {
  state: { data?: CalibrationCandidatesResponse };
}): number | false {
  const round = query.state.data?.round;
  if (!round) return CALIBRATION_CANDIDATES_POLL_MS;
  // Product decision 2 (2026-08-04). A failed round is terminal in exactly the
  // sense a full one is — it can never gain a candidate. Without this it would
  // fail the fullness test forever (candidateCount 0 of batchSize 12) and poll
  // a settled answer every 10s for as long as the tab stays open.
  if (isFailedCalibrationRound(round.status)) return false;
  return round.candidateCount >= round.batchSize ? false : CALIBRATION_CANDIDATES_POLL_MS;
}

export function useCalibrationCandidates(agentId: number, roundId: number) {
  return useQuery({
    queryKey: ['calibration-candidates', agentId, roundId],
    queryFn: () =>
      api.get<CalibrationCandidatesResponse>(
        `/api/sourcing-agents/${agentId}/calibration-rounds/${roundId}/candidates`
      ),
    enabled: !!agentId && !!roundId,
    refetchInterval: calibrationCandidatesRefetchInterval,
  });
}

/**
 * US-502/504c. The job's WHOLE spec — neither field capped — for the dossier's
 * comparison column.
 *
 * Deliberately NOT folded into `useCalibrationCandidates`. That query is
 * re-fetched every ten seconds while a round fills, so a 40KB JD on it would
 * charge ~2.4MB of re-sent constant data to the nine candidates in twelve that
 * are judged in fifteen seconds and never read closely — Direction B's own sin,
 * restated at the network layer.
 *
 * `enabled` is the fast-path guard: the caller passes false until the recruiter
 * has actually opened the dossier once. `staleTime: Infinity` and no polling
 * because the answer is per-round constant, so the whole round costs exactly
 * one request no matter how many candidates are researched — the query lives in
 * the shared client, above the per-candidate session that mounts and unmounts.
 */
export function useCalibrationJobSpec(agentId: number, roundId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['calibration-job-spec', agentId, roundId],
    queryFn: () =>
      api.get<CalibrationJobSpecResponse>(
        `/api/sourcing-agents/${agentId}/calibration-rounds/${roundId}/job-spec`,
      ),
    enabled: enabled && !!agentId && !!roundId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * US-379 (Task 6). The round-end reveal screen (RoundEndReveal) needs each
 * candidate's aiReviewScore alongside recruiterVerdict, which the blind-
 * judging GET .../candidates route above deliberately never returns — see
 * the new GET .../reveal route (Task 6 addition to sourcing-agents.ts) for
 * why that's a separate endpoint rather than a flag on the existing one.
 */
export interface CalibrationRevealCandidate {
  id: number;
  recruiterVerdict: 'match' | 'not_match' | 'unsure' | null;
  aiReviewScore: number | null;
}

export function useCalibrationReveal(agentId: number, roundId: number) {
  return useQuery({
    queryKey: ['calibration-reveal', agentId, roundId],
    queryFn: () =>
      api.get<{ candidates: CalibrationRevealCandidate[] }>(
        `/api/sourcing-agents/${agentId}/calibration-rounds/${roundId}/reveal`
      ),
    enabled: !!agentId && !!roundId,
  });
}

/**
 * US-379/380 (Task 6). Consumes the GET/accept/reject refinement-proposal
 * routes built in Task 5.
 */
/**
 * The wire type is the drift-guarded mirror of the shared contract the route
 * annotates its reply with — NOT a shape declared here. The locally-declared
 * version of this interface is what let the page render `proposedDiff` under
 * "Applied — here's what changed" while the server had quietly refused part of
 * it (review finding M7), and an earlier locally-declared calibration type is
 * what shipped a whole page against an API that had never been written.
 */
export type { RefinementProposalResponse, RefinementProposalRow, RefinementDiffEntry } from '../types/calibration-api';

/** Poll cadence while waiting for the worker to produce a proposal. */
export const REFINEMENT_PROPOSAL_POLL_MS = 3000;
/** ~60s of polling; past this the round almost certainly produced no proposal. */
export const REFINEMENT_PROPOSAL_MAX_POLLS = 20;

/**
 * Final review finding 4. Proposal generation is asynchronous (signal check,
 * history build, one or two LLM calls, DB insert) — several seconds at best.
 * A single fetch on mount always 404s, caches `null`, and shows the
 * "no signal" message forever with no retry. Keep polling while there's no
 * proposal, stop the moment one appears, and give up after a bounded number
 * of attempts rather than polling this route forever.
 *
 * React Query v5 function form: `(query) => number | false`.
 */
export function refinementProposalRefetchInterval(query: {
  state: { data?: unknown; dataUpdateCount: number };
}): number | false {
  if (query.state.data != null) return false;
  if (query.state.dataUpdateCount >= REFINEMENT_PROPOSAL_MAX_POLLS) return false;
  return REFINEMENT_PROPOSAL_POLL_MS;
}

/** What the calibration page needs to know about this ROUND's proposal. */
export interface RefinementProposalQuery {
  /** The proposal generated by this round, or null while there is none. */
  data: RefinementProposalResponse | null;
  isLoading: boolean;
  /**
   * True once polling has given up: this round is settled and produced no
   * proposal at all.
   *
   * Generation is asynchronous, so a single null is ambiguous between "still
   * generating" and "genuinely nothing". Collapsing the two into one message
   * is what let the page describe a state it could not distinguish; keeping
   * them apart is what lets it eventually say something definite instead of
   * hedging forever.
   */
  settledWithoutProposal: boolean;
}

/**
 * CRITICAL C-A (2026-08-04). `roundId` is REQUIRED, and it is part of the URL
 * path rather than an optional filter, because the agent-scoped version of
 * this call is precisely the bug: with no proposal for the round just judged,
 * the server answered with the newest proposal the agent had EVER had —
 * accepted days ago, for a different set of candidates — and the page rendered
 * it as "Applied — here's what changed".
 */
export function useRefinementProposal(
  agentId: number | undefined,
  roundId: number | undefined,
): RefinementProposalQuery {
  // Counted here rather than read off the query, because React Query's result
  // object exposes no `dataUpdateCount` (only `query.state` inside
  // `refetchInterval` has it) and a ref would not re-render the page when the
  // last poll lands.
  const [attempts, setAttempts] = useState(0);
  const enabled = !!agentId && !!roundId;

  const query = useQuery({
    queryKey: ['refinement-proposal', agentId, roundId],
    queryFn: async (): Promise<RefinementProposalResponse | null> => {
      // Raw fetch, not api.get() — the GET route returns a genuine 404 when
      // this round has no proposal (not a 200 with a null body), and that is a
      // normal, expected outcome (not every completed round produces one), not
      // an error. api.get()/the shared request() helper throws on any non-2xx,
      // which would surface as a React Query error state and show the wrong
      // "something went wrong" UI instead of the honest empty state.
      const response = await fetch(
        `/api/sourcing-agents/${agentId}/calibration-rounds/${roundId}/refinement-proposal`,
        { credentials: 'include' },
      );
      setAttempts((n) => n + 1);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    enabled,
    refetchInterval: refinementProposalRefetchInterval,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    settledWithoutProposal: enabled && query.data == null && attempts >= REFINEMENT_PROPOSAL_MAX_POLLS,
  };
}

export function useAcceptRefinementProposal(agentId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: number) =>
      api.post(`/api/sourcing-agents/${agentId}/refinement-proposal/${proposalId}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refinement-proposal', agentId] });
    },
  });
}

export function useRejectRefinementProposal(agentId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: number) =>
      api.post(`/api/sourcing-agents/${agentId}/refinement-proposal/${proposalId}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refinement-proposal', agentId] });
    },
  });
}

export function useJudgeCalibrationCandidate(agentId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      candidateId: number;
      verdict: string;
      reasonChip: string | null;
      note: string;
      /**
       * US-503. Omitted entirely on the fast path. The key must be ABSENT
       * rather than `undefined`-valued-then-serialised or `0`: the route reads
       * absent as "leave whatever is recorded alone" (GREATEST/COALESCE), and
       * the column keeps NULL distinct from zero so that "never researched"
       * and "opened and closed instantly" stay different facts.
       */
      researchSeconds?: number;
    }) =>
      api.patch(`/api/sourcing-agents/${agentId}/calibration-candidates/${input.candidateId}/judge`, {
        verdict: input.verdict,
        reasonChip: input.reasonChip,
        note: input.note,
        ...(input.researchSeconds === undefined ? {} : { researchSeconds: input.researchSeconds }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calibration-candidates', agentId] });
    },
  });
}

/**
 * Product decision 2 (2026-08-04). The retry behind a FAILED calibration round.
 *
 * A failed round is terminal — every candidate in it failed AI review, and
 * `runCalibrationRound`'s resume path adopts only 'in_progress' rounds — so
 * "retry" means "queue a NEW round". The route answers 202, because the round
 * row does not exist yet when it responds; the caller sends the recruiter to
 * the tuning-entry page, which polls until it does.
 *
 * No `onSuccess` invalidation: the round this page is looking at stays failed
 * forever, so refetching it would only re-render the same failure. The caller
 * navigates instead.
 */
export function useRetryCalibrationRound(agentId: number) {
  return useMutation({
    mutationFn: () => api.post(`/api/sourcing-agents/${agentId}/calibration-rounds/retry`),
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.deleteJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

export function useDiscoverJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.discoverJobs(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

// Application Management Hooks

export function useArchiveApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.archiveApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

export function useForceReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.forceReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
      // M8. Forcing a review changes what the sourcing surfaces render (a
      // failed row becomes pending), but this hook only invalidated the keys
      // its original caller — Application Detail — reads. SourcingPage's retry
      // therefore left the row reading "AI screening failed" until a manual
      // reload. Matched by predicate rather than key because the mutation is
      // given an applicationId and has no jobId to build the key from; a
      // blanket `queryKeys.jobs` invalidation would refetch every job list on
      // the client instead.
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('sourcing-matches'),
      });
    },
  });
}

export function useLookupBySrId(srApplicationId: string | null) {
  return useQuery({
    queryKey: srApplicationId ? queryKeys.lookupBySrId(srApplicationId) : ['lookupBySrId', 'disabled'],
    queryFn: () => api.lookupBySrId(srApplicationId!),
    enabled: !!srApplicationId,
    staleTime: 30_000,
    retry: false,
  });
}

export function useForceReviewBySrId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ srApplicationId, mode }: { srApplicationId: string; mode?: 'fetch' | 'best-effort' }) =>
      api.forceReviewBySrId(srApplicationId, { mode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

export function useReprocessBySrId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ srApplicationId, mode, reason }: { srApplicationId: string; mode?: 'fetch' | 'best-effort'; reason?: string }) =>
      api.reprocessBySrId(srApplicationId, { mode, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

// Candidates Hooks

export function useCandidates(page: number = 1, limit: number = 20, search?: string) {
  return useQuery({
    queryKey: [...queryKeys.candidates, { page, limit, search }],
    queryFn: () => api.getCandidates(page, limit, search),
  });
}

export function useCandidate(id: number) {
  return useQuery({
    queryKey: queryKeys.candidate(id),
    queryFn: () => api.getCandidate(id),
    enabled: id > 0,
  });
}

// Applications Hooks

export function useApplications(
  page: number = 1,
  limit: number = 20,
  search?: string,
  status?: ApplicationStatus,
  jobId?: number,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  stages?: string
) {
  return useQuery({
    queryKey: [...queryKeys.applications, { page, limit, search, status, jobId, sortBy, sortOrder, stages }],
    queryFn: () => api.getApplications(page, limit, search, status, jobId, sortBy, sortOrder, stages),
  });
}

export function useApplication(id: number) {
  return useQuery({
    queryKey: queryKeys.application(id),
    queryFn: () => api.getApplication(id),
    enabled: id > 0,
  });
}

export function useReprocessApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.reprocessApplication(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    },
  });
}

export function useJobApplications(
  jobId: number,
  page: number = 1,
  limit: number = 20,
  sortBy: string = 'createdAt',
  sortDir: string = 'desc'
) {
  return useQuery({
    queryKey: [...queryKeys.applications, 'job', jobId, { page, limit, sortBy, sortDir }],
    queryFn: () => api.getJobApplications(jobId, page, limit, sortBy, sortDir),
    enabled: jobId > 0,
  });
}

// Reviews Hooks

export interface UseReviewsParams {
  page?: number;
  limit?: number;
  search?: string;
  recommendation?: number;
  minScore?: number;
  maxScore?: number;
  hasScreeningKnockout?: boolean;
  staleFlag?: boolean;
}

export function useReviews(params: UseReviewsParams = {}) {
  const { page = 1, limit = 20, search, recommendation, minScore, maxScore, hasScreeningKnockout, staleFlag } = params;
  return useQuery({
    queryKey: [...queryKeys.reviews, { page, limit, search, recommendation, minScore, maxScore, hasScreeningKnockout, staleFlag }],
    queryFn: () => api.getReviews(page, limit, search, recommendation, minScore, maxScore, hasScreeningKnockout, staleFlag),
  });
}

export function useReview(id: number) {
  return useQuery({
    queryKey: queryKeys.review(id),
    queryFn: () => api.getReview(id),
    enabled: id > 0,
  });
}

export function useApplicationReviews(applicationId: number) {
  return useQuery({
    queryKey: [...queryKeys.reviews, 'application', applicationId],
    queryFn: () => api.getApplicationReviews(applicationId),
    enabled: applicationId > 0,
  });
}

// Feedback Hooks

export function useFeedback(applicationId: number) {
  return useQuery({
    queryKey: queryKeys.feedback(applicationId),
    queryFn: () => api.getFeedback(applicationId),
    enabled: applicationId > 0,
  });
}

export function useReviewVersions(applicationId: number) {
  return useQuery<ReviewVersionsResponse>({
    queryKey: queryKeys.reviewVersions(applicationId),
    queryFn: () => api.getReviewVersions(applicationId),
    enabled: applicationId > 0,
  });
}

export function useSubmitFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicationId,
      feedbackType,
      content,
    }: {
      applicationId: number;
      feedbackType: FeedbackType;
      content: string;
    }) => api.submitFeedback(applicationId, feedbackType, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.feedback(variables.applicationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewVersions(variables.applicationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.application(variables.applicationId) });
    },
  });
}

// Review Feedback Hooks (inline dashboard feedback)

export function useReviewFeedback(reviewId: number) {
  return useQuery<{ data: ReviewFeedbackEntry[]; reviewId: number; applicationId: number }>({
    queryKey: queryKeys.reviewFeedback(reviewId),
    queryFn: () => api.getReviewFeedback(reviewId),
    enabled: reviewId > 0,
  });
}

export function useSubmitReviewFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reviewId,
      data,
    }: {
      reviewId: number;
      data: ReviewFeedbackData;
    }) => api.submitReviewFeedback(reviewId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewFeedback(variables.reviewId) });
    },
  });
}

export function useQuickRate(applicationId: number) {
  return useQuery({
    queryKey: queryKeys.quickRate(applicationId),
    queryFn: () => api.getQuickRate(applicationId),
  });
}

export function useSubmitQuickRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, rating }: { applicationId: number; rating: QuickRating }) =>
      api.submitQuickRate(applicationId, rating),
    onSuccess: (_data, { applicationId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.quickRate(applicationId) });
    },
  });
}

export function useRollbackReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicationId,
      reviewId,
    }: {
      applicationId: number;
      reviewId: number;
    }) => api.rollbackReview(applicationId, reviewId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewVersions(variables.applicationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.application(variables.applicationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews });
    },
  });
}

// Health Check Hook

export function useHealthCheck() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.healthCheck(),
    refetchInterval: 30000, // Check every 30 seconds
  });
}

export function useSystemHealth() {
  return useQuery({
    queryKey: queryKeys.systemHealth,
    queryFn: () => api.getSystemHealth(),
    refetchInterval: 15000, // Refresh every 15 seconds for live monitoring
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

// Configuration Hooks

export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: async () => {
      const response = await api.getConfig();
      // Use the API URL for webhook, not the web app URL
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const hasSmartRecruitersKey = !!(response.config.sr_api_key && response.config.sr_api_key !== '********' && response.config.sr_api_key !== null);
      const hasOpenRouterKey = !!(response.config.openrouter_api_key && response.config.openrouter_api_key !== '********' && response.config.openrouter_api_key !== null);
      const hasWebhookSecret = !!(response.config.webhook_secret && response.config.webhook_secret !== '********' && response.config.webhook_secret !== null);

      return {
        ...response,
        webhookUrl: `${apiUrl}/webhooks/smartrecruiters`,
        hasSmartRecruitersKey,
        hasOpenRouterKey,
        hasWebhookSecret,
        isConfigured: hasSmartRecruitersKey && hasOpenRouterKey,
      };
    },
  });
}

export function useSaveConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConfigData) => api.saveConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.config });
    },
  });
}

export function useValidateConfig() {
  return useMutation({
    mutationFn: (data: Partial<ConfigData>) => api.validateConfig(data),
  });
}

// Dashboard Hooks

export function useDashboardMetrics() {
  return useQuery<DashboardMetricsResponse>({
    queryKey: ['dashboard', 'metrics'],
    queryFn: () => api.getDashboardMetrics(),
    refetchInterval: 30000, // 30 seconds
  });
}

export function useScreeningConflicts() {
  return useQuery<ScreeningConflictsResponse>({
    queryKey: queryKeys.screeningConflicts,
    queryFn: () => api.getScreeningConflicts(),
    refetchInterval: 60000, // 60 seconds
  });
}

export function useDashboardJobs() {
  return useQuery<{ jobs: DashboardJob[] }>({
    queryKey: queryKeys.jobsDashboard(),
    queryFn: () => api.getDashboardJobs(),
    refetchInterval: 60000, // 60 seconds
  });
}

export function useUnreadSignals() {
  return useQuery({
    queryKey: queryKeys.unreadSignals,
    queryFn: () => api.getUnreadSignals(),
    refetchInterval: 60000,
    retry: false,
  });
}

export function useMarkSignal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'read' | 'dismissed' }) => api.markSignal(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadSignals });
    },
  });
}

export function useOverrideScreeningKnockout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, overrideReason }: { reviewId: number; overrideReason?: string }) =>
      api.overrideScreeningKnockout(reviewId, overrideReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.screeningConflicts });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews });
    },
  });
}

// Dead Letter Queue Hooks

export function useDeadLetterEntries(params: DeadLetterListParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.deadLetter, params],
    queryFn: () => api.getDeadLetterEntries(params),
  });
}

export function useDeadLetterEntry(id: number) {
  return useQuery({
    queryKey: queryKeys.deadLetterEntry(id),
    queryFn: () => api.getDeadLetterEntry(id),
    enabled: id > 0,
  });
}

export function useResolveDeadLetter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, resolved, notes }: { id: number; resolved: string; notes?: string }) =>
      api.resolveDeadLetterEntry(id, { resolved, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deadLetter });
      queryClient.invalidateQueries({ queryKey: queryKeys.deadLetterStats });
    },
  });
}

export function useRetryDeadLetter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      api.retryDeadLetterEntry(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deadLetter });
      queryClient.invalidateQueries({ queryKey: queryKeys.deadLetterStats });
    },
  });
}

export function useDeadLetterStats() {
  return useQuery<DeadLetterStats>({
    queryKey: queryKeys.deadLetterStats,
    queryFn: () => api.getDeadLetterStats(),
    refetchInterval: 30000,
  });
}

// Setup Verification Hooks

export function useSetupVerify() {
  return useQuery<SetupVerifyResponse>({
    queryKey: queryKeys.setupVerify,
    queryFn: () => api.verifySetup(),
  });
}

// AI Cost Metrics Hooks

export function useAiCostMetrics() {
  return useQuery<AiCostMetrics>({
    queryKey: queryKeys.aiCostMetrics,
    queryFn: () => api.getAiCostMetrics(),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 15000, // Consider data stale after 15 seconds
  });
}

// AI Cost Dashboard Hook (Task #52)

export function useAICosts() {
  return useQuery<AICostDashboardData>({
    queryKey: queryKeys.aiCostDashboard,
    queryFn: () => api.getAICosts(),
    refetchInterval: 60000, // Refresh every 60 seconds
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}

// Sync Status Hooks

export function useSyncStatus(reviewId: number) {
  return useQuery<SyncStatusResponse>({
    queryKey: queryKeys.syncStatus(reviewId),
    queryFn: () => api.getSyncStatus(reviewId),
    enabled: reviewId > 0,
  });
}

export function useSyncReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, force = false }: { reviewId: number; force?: boolean }) =>
      api.syncReview(reviewId, force),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.syncStatus(variables.reviewId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews });
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    },
  });
}

export function useSyncApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, force = false }: { applicationId: number; force?: boolean }) =>
      api.syncApplication(applicationId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviews });
    },
  });
}

// Job Criteria Hooks

export function useJobCriteria(jobId: number) {
  return useQuery({
    queryKey: queryKeys.jobCriteria(jobId),
    queryFn: () => api.getJobCriteria(jobId),
    enabled: jobId > 0,
  });
}

export function useJobCriteriaChangelog(jobId: number) {
  return useQuery({
    queryKey: queryKeys.jobCriteriaChangelog(jobId),
    queryFn: () => api.getJobCriteriaChangelog(jobId),
    enabled: jobId > 0,
  });
}

export function useUpdateJobCriteria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      criteriaText,
      changeReason,
      source,
    }: {
      jobId: number;
      criteriaText: string;
      changeReason?: string;
      source?: string;
    }) => api.updateJobCriteria(jobId, criteriaText, changeReason, source),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteria(variables.jobId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteriaChangelog(variables.jobId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.job(variables.jobId) });
    },
  });
}

export function useSyncJobCriteria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: number) => api.syncJobCriteria(jobId),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteria(jobId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobCriteriaChangelog(jobId) });
    },
  });
}

export function useToggleJobSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, enabled }: { jobId: number; enabled: boolean }) =>
      api.toggleJobSync(jobId, enabled),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
      queryClient.invalidateQueries({ queryKey: queryKeys.job(variables.jobId) });
    },
  });
}

export interface GenerateCriteriaRequest {
  jobDescription: string;
  recruiterContext: string;
  step: 'clarify' | 'generate';
  clarifications?: Array<{ question: string; answer: string }>;
  forceInclude?: string;
}

export function useGenerateCriteria() {
  return useMutation({
    mutationFn: ({ jobId, body }: { jobId: number; body: GenerateCriteriaRequest }) =>
      api.generateCriteria(jobId, body),
  });
}

// US-081: Rejection Reasons
export function useRejectionReasons() {
  return useQuery({
    queryKey: queryKeys.rejectionReasons,
    queryFn: async () => {
      const response = await fetch('/api/rejection-reasons');
      const json = await response.json();
      return json.data as RejectionReason[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

// US-082: Merge Audit
export function useMergeAudit(page = 1) {
  return useQuery({
    queryKey: [...queryKeys.mergeAudit, page],
    queryFn: async () => {
      const response = await fetch(`/api/candidates/merge-audit?page=${page}&limit=20`);
      return response.json() as Promise<PaginatedResponse<CandidateMergeAudit>>;
    },
  });
}

// US-089: Reporting Hooks

export function useReportsDashboard(days: number = 7) {
  return useQuery<DashboardAnalytics>({
    queryKey: [...queryKeys.reportsDashboard, { days }],
    queryFn: () => api.getReportsDashboard(days),
    refetchInterval: 60000,
    staleTime: 30000,
  });
}

export function useReportSnapshots(page: number = 1, limit: number = 20, reportType?: string) {
  return useQuery<PaginatedResponse<ReportSnapshotSummary>>({
    queryKey: [...queryKeys.reportSnapshots, { page, limit, reportType }],
    queryFn: () => api.getReportSnapshots(page, limit, reportType),
  });
}

export function useReportSnapshot(id: number) {
  return useQuery<ReportSnapshotFull>({
    queryKey: queryKeys.reportSnapshot(id),
    queryFn: () => api.getReportSnapshot(id),
    enabled: id > 0,
  });
}

// Notification Settings Hooks

export function useNotificationSettings(jobId: number) {
  return useQuery({
    queryKey: [...queryKeys.jobs, jobId, 'notification-settings'],
    queryFn: () => api.getNotificationSettings(jobId),
    enabled: jobId > 0,
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jobId,
      members,
    }: {
      jobId: number;
      members: Array<{ id: number; notifyOnReview: boolean }>;
    }) => api.updateNotificationSettings(jobId, members),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.jobs, variables.jobId, 'notification-settings'],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.job(variables.jobId) });
    },
  });
}

export function useRefreshHiringTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: number) => api.refreshHiringTeam(jobId),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.jobs, jobId, 'notification-settings'],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.job(jobId) });
    },
  });
}

export function useGenerateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportType,
      startDate,
      endDate,
      jobId,
    }: {
      reportType: ReportType;
      startDate?: string;
      endDate?: string;
      jobId?: number;
    }) => api.generateReport(reportType, startDate, endDate, jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reportSnapshots });
    },
  });
}

// AI Accuracy Report Hook

export function useAiAccuracyReport() {
  return useQuery<AiAccuracyReportResponse>({
    queryKey: queryKeys.aiAccuracyReport,
    queryFn: () => api.getAiAccuracyReport(),
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// US-230b: Funnel Insights Panel
export function useFunnelInsights(jobId: number) {
  return useQuery({
    queryKey: queryKeys.funnelInsights(jobId),
    queryFn: () => api.getFunnelInsights(jobId),
    enabled: jobId > 0,
    staleTime: 60_000, // 1 minute
  });
}

// US-150: Manual Resume Upload
export function useUpdateApplicationResume() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, resumeText }: { applicationId: number; resumeText: string }) =>
      updateApplicationResume(applicationId, resumeText),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.application(variables.applicationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

export function useUploadApplicationResume() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, file }: { applicationId: number; file: File }) =>
      uploadApplicationResume(applicationId, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.application(variables.applicationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.applications });
      queryClient.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
}

// US-237: Model Catalog hook
export function useModelCatalog() {
  return useQuery({
    queryKey: queryKeys.modelCatalog,
    queryFn: () => api.getModelCatalog(),
    staleTime: 5 * 60 * 1000, // 5 minutes — catalog rarely changes
  });
}

// --- User Management (in-org) ---

export function useOrgUsers() {
  return useQuery({
    queryKey: queryKeys.orgUsers,
    queryFn: () => api.get<OrgUser[]>('/api/users'),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: string }) =>
      api.post('/api/users/invite', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; role?: string }) =>
      api.patch(`/api/users/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers }),
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch(`/api/users/${id}/deactivate`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers }),
  });
}

export function useResendInvite() {
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/users/${id}/resend-invite`, {}),
  });
}

// --- Admin (super_admin only) ---

export function useAdminOrganizations() {
  return useQuery({
    queryKey: queryKeys.adminOrganizations,
    queryFn: () => api.get<AdminOrg[]>('/api/admin/organizations'),
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { orgName: string; adminEmail: string }) =>
      api.post('/api/admin/organizations', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations }),
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; status?: string }) =>
      api.patch(`/api/admin/organizations/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations }),
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: () => api.get<AdminUser[]>('/api/admin/users'),
  });
}
